import React from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

interface MyTimeWorkTimeBalanceFrameBoxProps {
  data: {
    startBalance?: number;
    actualBalance?: number;
    endBalance?: number;
  };
  loading: boolean;
  minHeight?: string | number;
}

export const MyTimeWorkTimeBalanceFrameBox: React.FC<
  MyTimeWorkTimeBalanceFrameBoxProps
> = ({ data, loading, minHeight }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const whiteColor = theme.palette.white.main;

  const boxSeparator = () => (
    <Box
      style={{
        backgroundColor: whiteColor,
        width: "auto",
        height: "1px",
      }}
    />
  );

  const formatValue = (value: number | undefined) => {
    if (value === undefined) return "";
    if (value % 1 !== 0) {
      const formattedValue = value.toFixed(3);
      return formattedValue.endsWith("00")
        ? Number.parseFloat(formattedValue).toFixed(1)
        : formattedValue;
    } else {
      return value.toString();
    }
  };

  return (
    <Box
      minHeight={minHeight}
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        backgroundColor: theme.palette.blueBase.main,
        borderRadius: "8px",
        color: whiteColor,
      }}
    >
      <Box padding="12px 16px">
        <Typography variant="h6" color={whiteColor}>
          {t("MY_TIME_AVAILABLE_WORKING_BALANCE")}
        </Typography>
        <Box height="8px" />
        <Typography variant="body1" color={whiteColor}>
          {t("MY_TIME_OPENING_BALANCE")}
        </Typography>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Typography
            variant="body1"
            style={{
              color: whiteColor,
              fontWeight: 700,
              textTransform: "lowercase",
            }}
          >
            {loading
              ? t("OC_LOADING")
              : `${formatValue(data?.startBalance)} ${t("OC_HOUR")}`}
          </Typography>
        </Box>
        <Box height="8px" />
        {boxSeparator()}
        <Box height="8px" />
        <Typography variant="body1" color={whiteColor}>
          {t("MY_TIME_CURRENT_BALANCE")}
        </Typography>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Typography
            variant="body1"
            style={{
              color: whiteColor,
              fontWeight: 700,
              textTransform: "lowercase",
            }}
          >
            {loading
              ? t("OC_LOADING")
              : `${formatValue(data?.actualBalance)} ${t("OC_HOUR")}`}
          </Typography>
        </Box>
        <Box height="8px" />
        {boxSeparator()}
        <Box height="8px" />
        <Typography variant="body1" color={whiteColor}>
          {t("MY_TIME_CLOSING_BALANCE")}
        </Typography>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Typography
            variant="h4"
            style={{
              color: whiteColor,
              fontWeight: 500,
              textTransform: "lowercase",
            }}
          >
            {loading
              ? t("OC_LOADING")
              : `${formatValue(data?.endBalance)} ${t("OC_HOUR")}`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default MyTimeWorkTimeBalanceFrameBox;
